import React, { useEffect, useRef, useState } from "react";

// ── Observador único y compartido ────────────────────────────────────────────
// La versión anterior vivía dentro de pages/Home.js y creaba un
// IntersectionObserver por elemento. Ahora que el revelado se usa en las trece
// páginas, un solo observador para todos evita multiplicar los callbacks de
// scroll.
let observer = null;
const callbacks = new WeakMap();

function getObserver() {
  if (observer) return observer;
  observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const cb = callbacks.get(entry.target);
        if (cb) {
          cb();
          callbacks.delete(entry.target);
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: "0px 0px -10% 0px" }
  );
  return observer;
}

/**
 * Revela sus hijos al entrar en pantalla: fundido + desplazamiento vertical.
 *
 * @param {number} delay     Retardo en ms. Para escalonar hermanos.
 * @param {number} distance  Recorrido en px (los titulares lucen desde más lejos).
 * @param {string} as        Etiqueta a renderizar. Por defecto `div`.
 *
 * Si el navegador no soporta IntersectionObserver, el contenido se muestra sin
 * animación en lugar de quedarse invisible para siempre.
 */
export default function Reveal({
  children,
  className = "",
  delay = 0,
  distance = 30,
  as: Tag = "div",
  style,
  ...rest
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }

    // Si ya está en pantalla al montar (contenido sobre el pliegue, o una
    // sección que llega tarde del backend), se revela sin esperar a un scroll.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setShown(true);
      return;
    }

    callbacks.set(el, () => setShown(true));
    getObserver().observe(el);

    return () => {
      callbacks.delete(el);
      getObserver().unobserve(el);
    };
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${shown ? "in-view" : ""} ${className}`}
      style={{
        "--reveal-delay": `${delay}ms`,
        "--reveal-distance": `${distance}px`,
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * Escalona la entrada de sus hijos directos.
 *
 * Antes el revelado envolvía secciones enteras, así que el titular, el texto y
 * las seis tarjetas aparecían de golpe como un bloque. Escalonarlos es lo que
 * da la cadencia característica de Apple.
 */
export function RevealGroup({
  children,
  className = "",
  step = 70,
  distance = 24,
  as: Tag = "div",
  ...rest
}) {
  const items = React.Children.toArray(children);
  return (
    <Tag className={className} {...rest}>
      {items.map((child, i) => (
        <Reveal key={child.key ?? i} delay={i * step} distance={distance}>
          {child}
        </Reveal>
      ))}
    </Tag>
  );
}
